import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Divider } from "@mui/material";
import CommonTable from "../common/table/commonTable";
import { createHeaderWithSort } from "../common/table/columns";
import {
  white,
  darkDenium,
  linkColor,
  linkHoverColor,
} from "../../constants/colors";

const CareGuideNameCell = ({ val, id, onNameClick }) => (
  <Typography
    onClick={() => onNameClick(val, id)}
    sx={{
      color: linkColor,
      fontWeight: 600,
      cursor: "pointer",
      textDecoration: "underline",
      "&:hover": { color: linkHoverColor },
    }}
  >
    {val}
  </Typography>
);

const CenteredCell = ({ val, loading }) => (
  <Box textAlign="center">{loading ? "-" : (val ?? "-")}</Box>
);

const HeaderCenterBox = ({ title, showSortIcon }) => (
  <Box sx={{ display: "flex", justifyContent: "center" }}>
    {createHeaderWithSort(title, showSortIcon)}
  </Box>
);

const NameCellRenderer = (onNameClick) => {
  function Renderer({ getValue, row }) {
    return (
      <CareGuideNameCell
        val={getValue()}
        id={row.original.id}
        onNameClick={onNameClick}
      />
    );
  }
  Renderer.displayName = "NameCellRenderer";
  Renderer.propTypes = {
    getValue: PropTypes.func.isRequired,
    row: PropTypes.shape({
      original: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    }).isRequired,
  };
  return Renderer;
};

const ValueCellRenderer = (loading) => {
  function Renderer({ getValue }) {
    return <CenteredCell val={getValue()} loading={loading} />;
  }
  Renderer.displayName = "ValueCellRenderer";
  Renderer.propTypes = {
    getValue: PropTypes.func.isRequired,
  };
  return Renderer;
};

const CareGuidePerformanceReport = ({ data, loading, onNameClick }) => {
  const columns = useMemo(
    () => [
      {
        header: createHeaderWithSort("Care Guide", true),
        accessorKey: "name",
        cell: NameCellRenderer(onNameClick),
      },
      {
        header: <HeaderCenterBox title="Total" showSortIcon />,
        accessorKey: "total",
        cell: ValueCellRenderer(loading),
      },
      {
        header: <HeaderCenterBox title="Completed" showSortIcon />,
        accessorKey: "completed",
        cell: ValueCellRenderer(loading),
      },
      {
        header: <HeaderCenterBox title="Not Completed" showSortIcon />,
        accessorKey: "notCompleted",
        cell: ValueCellRenderer(loading),
      },
    ],
    [loading, onNameClick],
  );

  const displayData = loading
    ? [{ name: "Unassigned", total: "", completed: "", notCompleted: "" }]
    : data;

  return (
    <Box sx={{ mb: 4, backgroundColor: white, borderRadius: "12px" }}>
      <Typography
        sx={{
          color: darkDenium,
          fontSize: "16px",
          fontWeight: 500,
          mb: 1,
          pt: 2,
          pb: 1,
          px: 2,
        }}
      >
        Care Guide Performance Report
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ p: 2, pt: 1 }}>
        <CommonTable
          columns={columns}
          data={displayData}
          tableHeight={"300px"}
          paginate={false}
          sx={{
            "& .MuiTableHead-root .MuiTableCell-root": {
              color: "#403C6F",
              fontSize: "14px",
              fontWeight: 600,
              py: 1.25,
              backgroundColor: "#CFCEDA",
              border: "none",
            },
            "& .MuiTableCell-root": {
              color: "#403C6F !important",
              borderBottom: "none !important",
            },
            "& .MuiTableRow-root::after": {
              display: "none !important",
            },
          }}
        />
      </Box>
    </Box>
  );
};

CareGuidePerformanceReport.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      total: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      completed: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      notCompleted: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  ).isRequired,
  loading: PropTypes.bool,
  onNameClick: PropTypes.func.isRequired,
};

HeaderCenterBox.propTypes = {
  title: PropTypes.string.isRequired,
  showSortIcon: PropTypes.bool,
};

CenteredCell.propTypes = {
  val: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  loading: PropTypes.bool,
};

CareGuideNameCell.propTypes = {
  val: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onNameClick: PropTypes.func.isRequired,
};

export default CareGuidePerformanceReport;
